// =====================================================================================
// FILE: src/constants/columns.js (HARDCODED COLUMNS PER TABLE - DO NOT CHANGE)
// =====================================================================================

// ✅ ALL AVAILABLE COLUMNS (master list) - ORDER MATTERS!
export const COLS = [
  { key: "select", label: "Select", w: 65, sticky: true },
  { key: "drag", label: "", w: 70, sticky: true },
  { key: "cue", label: "Cue", w: 70, sticky: true },
  { key: "name", label: "Name", w: 220 },
  { key: "playerNumber", label: "Player Number", w: 130 },
  { key: "clientSponsor", label: "Client or Sponsor Name", w: 260 },
  { key: "contentStatus", label: "Content Status", w: 240 },
  { key: "notes", label: "Lumen8 Comments", w: 320 },
  { key: "bigGfxVideos", label: "Big Screen - GFX & Videos", w: 240 },
  { key: "cornerGfxVideos", label: "Corner Screen - GFX & Videos", w: 240 },
  { key: "ledAllTypes", label: "LED - All Types", w: 200 },
  { key: "bigTvc", label: "Big Screen - TVC", w: 220 },
  { key: "cornerTvc", label: "Corner Screen - TVC", w: 220 },
  { key: "superFull", label: "Super Fullscreen", w: 240 },
  { key: "iptvGfx", label: "IPTV - GFX & Videos", w: 260 },
  { key: "rotations", label: "Rotations", w: 120 },
  { key: "bonusPriority", label: "Bonus Priority", w: 130 },
  { key: "selectTiming", label: "Select Timing", w: 240 },
  { key: "selectLocation", label: "Select Location", w: 300 },
  { key: "allocationId", label: "Allocation ID", w: 160 },
  { key: "mcgTiledLed", label: "MCG Tiled LED", w: 220 },
  { key: "marvelTiledLed", label: "Marvel Tiled LED", w: 220 },
  { key: "super169", label: "Super 20:9", w: 240 },
  { key: "superSide", label: "Super Side Panels", w: 220 },
  { key: "mcgInterchange", label: "MCG Interchange", w: 200 },
  { key: "marvelInterchange", label: "Marvel Interchange", w: 200 },
  { key: "marvel360", label: "Marvel 360 LED", w: 190 },
  { key: "mcg360", label: "MCG 360 LED", w: 180 },
  { key: "clubInstructions", label: "Club Instructions", w: 320 },
  // ✅ Actions column removed - now using right-click context menu
];

// =====================================================================================
// HARDCODED COLUMNS PER TABLE - EXPLICIT CONFIGURATION
// =====================================================================================

// Standard columns that appear in most tables (in order)
const STANDARD_COLS = ["select", "drag", "cue", "name", "clientSponsor", "contentStatus", "notes"];
const END_COLS = ["clubInstructions"];  // ✅ Actions removed - using right-click menu

// ✅ GAME TIMELINE (Out of Game) - mode=out
// Shows: Big Screen GFX (MCG) / Corner Screen GFX (MRV), LED All Types (NO TVC, NO IPTV)
const GAME_TIMELINE_COLS_MRV = [
  ...STANDARD_COLS,
  "cornerGfxVideos",   // Corner Screen - GFX & Videos (Marvel)
  "superFull",         // SuperScreen – Fullscreen / Takeovers
  "super169",          // SuperScreen - 20:9 Safe Graphics
  "superSide",         // SuperScreen - Side Panels
  "ledAllTypes",       // LED - All Types
  ...END_COLS,
];

const GAME_TIMELINE_COLS_MCG = [
  ...STANDARD_COLS,
  "bigGfxVideos",      // Big Screen - GFX & Videos (MCG)
  "ledAllTypes",       // LED - All Types
  ...END_COLS,
];

// ✅ TVC CONTENT (In Game) - mode=ingame
// Shows: Corner Screen TVC (Marvel), Big Screen TVC (MCG) - NO IPTV, NO LED
const TVC_CONTENT_COLS_MRV = [
  ...STANDARD_COLS,
  "cornerTvc",         // Corner Screen - TVC (Marvel)
  ...END_COLS,
];

const TVC_CONTENT_COLS_MCG = [
  ...STANDARD_COLS,
  "bigTvc",            // Big Screen - TVC (MCG)
  ...END_COLS,
];

// ✅ IN-GAME COMMERCIAL (LED Entitlements renamed) - mode=led
// Shows: LED All Types, Rotations, Bonus Priority - NO IPTV, NO Allocation ID
const IN_GAME_COMMERCIAL_COLS = [
  ...STANDARD_COLS,
  "ledAllTypes",       // LED - All Types
  "rotations",         // Rotations
  "bonusPriority",     // Bonus Priority (1-5 ordering for bonus spots)
  ...END_COLS,
];

// ✅ TEAM LIST - mode=team
// MCG: Big Screen - GFX & Videos, LED All Types
// Marvel: Corner Screen - GFX & Videos, SuperScreens (Full, 16:9, Side), LED All Types
const TEAM_LIST_COLS_MRV = [
  "select", "drag", "cue", "name", "playerNumber", "clientSponsor", "contentStatus", "notes",
  "cornerGfxVideos",   // Corner Screen - GFX & Videos (Marvel)
  "superFull",         // SuperScreen – Fullscreen / Takeovers
  "super169",          // SuperScreen - 20:9 Safe Graphics
  "superSide",         // SuperScreen - Side Panels
  "ledAllTypes",       // LED - All Types
  ...END_COLS,
];

const TEAM_LIST_COLS_MCG = [
  "select", "drag", "cue", "name", "playerNumber", "clientSponsor", "contentStatus", "notes",
  "bigGfxVideos",      // Big Screen - GFX & Videos (MCG)
  "ledAllTypes",       // LED - All Types
  ...END_COLS,
];

// ✅ IPTV - mode=iptv
// Shows: IPTV GFX, Timing, Location - NO OTHER CONTENT COLUMNS (Rotations hidden)
const IPTV_COLS = [
  "select", "drag", "cue", "name", "clientSponsor", "contentStatus", "notes",
  "iptvGfx",           // IPTV - GFX & Videos
  "selectTiming",      // Select Timing
  "selectLocation",    // Select Location
  "clubInstructions",  // ✅ Actions removed - using right-click menu
];

// =====================================================================================
// HELPER FUNCTIONS
// =====================================================================================

export function getVenueFromViewName(viewName) {
  const v = String(viewName || "").trim();
  if (!v) return "ALL";
  if (v.endsWith("_MCG")) return "MCG";
  if (v.endsWith("_MRV")) return "MRV";
  // ✅ Handle Team List views: "MCG Team List" / "Marvel Team List"
  if (v.startsWith("MCG ")) return "MCG";
  if (v.startsWith("Marvel ")) return "MRV";
  return "ALL";
}

// ✅ Get visible columns based on table mode and venue
export function getVisibleColsForVenue(venue, mode = {}) {
  const { isInGame = false, isInGameCommercial = false, isLed = false, isTeam = false, isIptv = false } = mode;
  
  let allowedKeys;

  // ============================================================================
  // DETERMINE WHICH COLUMNS TO SHOW BASED ON TABLE
  // ============================================================================
  
  if (isIptv) {
    // ✅ IPTV TABLE - Fixed columns
    allowedKeys = new Set(IPTV_COLS);
  } 
  else if (isTeam) {
    // ✅ TEAM LIST TABLE
    if (venue === "MCG") {
      allowedKeys = new Set(TEAM_LIST_COLS_MCG);
    } else {
      // MRV or ALL defaults to Marvel columns
      allowedKeys = new Set(TEAM_LIST_COLS_MRV);
    }
  }
  else if (isLed || isInGameCommercial) {
    // ✅ IN-GAME COMMERCIAL (LED) TABLE - LED All Types only, NO IPTV
    allowedKeys = new Set(IN_GAME_COMMERCIAL_COLS);
  }
  else if (isInGame) {
    // ✅ TVC CONTENT TABLE - TVC columns only
    if (venue === "MCG") {
      allowedKeys = new Set(TVC_CONTENT_COLS_MCG);
    } else {
      // MRV or ALL defaults to Marvel columns
      allowedKeys = new Set(TVC_CONTENT_COLS_MRV);
    }
  }
  else {
    // ✅ GAME TIMELINE (OUT OF GAME) TABLE - Default
    if (venue === "MCG") {
      allowedKeys = new Set(GAME_TIMELINE_COLS_MCG);
    } else {
      // MRV or ALL defaults to Marvel columns
      allowedKeys = new Set(GAME_TIMELINE_COLS_MRV);
    }
  }

  // Filter COLS to only include allowed keys, preserving order from COLS
  return COLS.filter((c) => allowedKeys.has(c.key));
}

export function gridTemplate(cols) {
  return cols.map((c) => `${c.w}px`).join(" ");
}

export function minWidth(cols) {
  return cols.reduce((sum, c) => sum + c.w, 0);
}

export function buildStickyLeft(cols) {
  let left = 0;
  const map = {};
  for (const c of cols) {
    if (c.sticky) {
      map[c.key] = left;
      left += c.w;
    }
  }
  return map;
}
